import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { makeStyles } from '@material-ui/core';
import useRequest from '@hooks/useRequest';
import {
  DueDateInput,
  EstimateBudgetRangeInput,
  ProjectNameInput,
  ProjectPurposeSelect
} from '@routes/NewRequest/FormComponents';
import { getMinDateFromToday } from '@utils/date';
import { CommonRequestForm, DesignStepProps } from './types';

const CommonStep: React.FC<DesignStepProps> = ({
  setDisabledNextButton,
  nextButtonAction
}) => {
  const [isValid, setIsValid] = useState<boolean>(false);
  const { requestContainer } = useDesignRequestStyles();
  const { requestState, onUpdateRfqInfo } = useRequest();
  const formProps = useForm<CommonRequestForm>({
    mode: 'onChange',
    defaultValues: {
      projectName: requestState.projectName,
      detailedRequest: requestState.detailedRequest,
      dueDate:
        requestState.dueDate !== ''
          ? requestState.dueDate
          : getMinDateFromToday(7).toISOString(),
      maxEstimatedBudget:
        requestState.maxEstimatedBudget === 0
          ? 0
          : requestState.maxEstimatedBudget,
      projectPurpose: requestState.projectPurpose,
      projectPurposeDetail: requestState.projectPurposeDetail,
      budgetOpen: requestState.budgetOpen ?? false,
      dueNegotiable: requestState.dueNegotiable ?? false,
      referenceFiles: requestState.referenceFiles ?? []
    }
  });
  const { register, errors, watch } = formProps;
  const form = watch();
  const currentFormValues = useRef<CommonRequestForm>({
    projectName: requestState.projectName,
    detailedRequest: requestState.detailedRequest,
    dueDate:
      requestState.dueDate !== ''
        ? requestState.dueDate
        : getMinDateFromToday(7).toISOString(),
    maxEstimatedBudget:
      requestState.maxEstimatedBudget === 0
        ? 0
        : requestState.maxEstimatedBudget,
    projectPurpose: requestState.projectPurpose,
    projectPurposeDetail: requestState.projectPurposeDetail,
    budgetOpen: requestState.budgetOpen,
    dueNegotiable: requestState.dueNegotiable ?? false,
    referenceFiles: requestState.referenceFiles ?? []
  });

  const onSubmit = useCallback(() => {
    onUpdateRfqInfo(currentFormValues.current);
    //eslint-disable-next-line
  }, [onUpdateRfqInfo, currentFormValues.current]);

  useEffect(() => {
    currentFormValues.current = {
      ...requestState,
      ...form,
      detailedRequest: form.detailedRequest
        ? form.detailedRequest
        : requestState.detailedRequest
    };
  }, [form, requestState]);

  useEffect(() => {
    nextButtonAction.current.PROJECT_COMMON = onSubmit;
  }, [onSubmit, nextButtonAction]);

  useEffect(() => {
    if (!setDisabledNextButton) return;

    setDisabledNextButton(!isValid);
  }, [isValid, setDisabledNextButton]);

  useEffect(() => {
    const { projectName, projectPurpose, dueDate, maxEstimatedBudget } = form;

    const noError = (Object.keys(errors) ?? []).length === 0;

    if (
      projectName &&
      projectPurpose &&
      dueDate &&
      maxEstimatedBudget &&
      noError
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [form, errors]);

  useEffect(() => {
    return onSubmit;
    //eslint-disable-next-line
  }, []);

  return (
    <div className={requestContainer}>
      <FormProvider {...formProps}>
        <form>
          <ProjectNameInput
            error={errors.projectName?.message}
            ref={register({
              required: '프로젝트명을 입력해주세요.',
              validate: {
                maxLength: (e) =>
                  e?.length > 20 ? '20자 이내로 입력해주세요' : true
              }
            })}
          />
          <ProjectPurposeSelect error={errors.projectPurpose?.message} />
          <DueDateInput error={errors.dueDate?.message} />
          <EstimateBudgetRangeInput
            maxError={errors?.maxEstimatedBudget?.message}
          />
        </form>
      </FormProvider>
    </div>
  );
};

export default CommonStep;

const useDesignRequestStyles = makeStyles((theme) => ({
  requestContainer: {
    width: '100%',
    maxWidth: '464px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '72px',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0
    }
  }
}));
